import {
  REQUIRED_STAGES,
  STAGE_ORDINAL,
  STAGE_SEQUENCE,
  isProgressiveStage,
  isWorkflowStage,
  nextStage,
  type WorkflowStage,
} from '../constants/stages.js';

/**
 * Which moves the workflow allows, and what each one MEANS.
 *
 * A garment walks the shop's enabled stages in order. The rule that earns this file is that it
 * may not skip: a bill that goes from `booked` straight to `delivered` is either a mis-tap or a
 * shop covering a missed step, and either way the stage history — which task 26 turns into
 * "average days per stage" and task 20 pays karigars from — becomes fiction. So a forward move
 * goes to exactly the next ENABLED stage, and a shop that hides `handwork` advances from
 * `stitching` to `finishing` because that is what "next" means for them.
 *
 * Backwards is a different question and gets a different answer. Work does fail QC and go back
 * to stitching, and a manager does tap the wrong card. Both are legitimate, and refusing them
 * would only teach the shop to lie to the board. So a backward move is allowed — to ANY earlier
 * enabled stage, not merely the previous one, because rework goes back to where the fault was —
 * but it always carries a reason, it is a separate permission (`items.stage.revert`), and it is
 * audit-critical. `rework` and `revert` are the same mechanism named for the two things a shop
 * means by it: the garment failed, versus the board was wrong.
 *
 * This lives in shared code because both sides need the same answer: the API to enforce it, and
 * the browser to know which control to offer before it asks — and to roll a card back to where
 * it was when the server disagrees.
 */

/** What a move IS, once classified. All three are recorded in the item's stage history. */
export const STAGE_MOVE_KINDS = ['advance', 'revert', 'rework'] as const;
export type StageMoveKind = (typeof STAGE_MOVE_KINDS)[number];

/** Why a move was refused. The client maps these onto its own copy; the API sends a message. */
export type StageMoveRefusal =
  | 'unknown_stage'
  | 'stage_disabled'
  | 'same_stage'
  | 'item_cancelled'
  | 'cancel_is_not_a_move'
  | 'terminal_stage'
  | 'skips_stages'
  | 'reason_required';

export interface StageMoveRequest {
  from: WorkflowStage;
  to: WorkflowStage;
  /** This shop's workflow, in order. Defaults to the full canonical sequence. */
  enabledStages?: readonly WorkflowStage[];
  /** Required for every backward move. Blank counts as absent. */
  reason?: string | null;
  /** True when the shop is sending failed work back, rather than correcting a mis-tap. */
  rework?: boolean;
}

export type StageMoveDecision =
  | {
      readonly ok: true;
      readonly kind: StageMoveKind;
      readonly from: WorkflowStage;
      readonly to: WorkflowStage;
      readonly requiresReason: boolean;
    }
  | { readonly ok: false; readonly refusal: StageMoveRefusal; readonly message: string };

function refuse(refusal: StageMoveRefusal, message: string): StageMoveDecision {
  return { ok: false, refusal, message };
}

function isEnabled(stage: WorkflowStage, enabledStages: readonly WorkflowStage[]): boolean {
  return enabledStages.includes(stage);
}

/**
 * Judge one move. The single gate every stage change on the server passes through, and the same
 * one the browser consults before it offers a control.
 */
export function classifyStageMove(request: StageMoveRequest): StageMoveDecision {
  const { from, to } = request;
  const enabledStages = request.enabledStages ?? STAGE_SEQUENCE;

  // Guarded rather than trusted to the type: `to` arrives from a request body on the server
  // and from a drop target in the browser, and both can be wrong at runtime.
  if (!isWorkflowStage(from) || !isWorkflowStage(to)) {
    return refuse('unknown_stage', 'That is not a workflow stage');
  }

  // Cancelling is not a stage move. It takes a reason, reverses the customer's statistics and
  // is its own audited endpoint — routing it through here would skip all of that.
  if (to === 'cancelled') {
    return refuse('cancel_is_not_a_move', 'Cancel the garment instead — cancelling is not a stage');
  }
  if (from === 'cancelled') {
    return refuse('item_cancelled', 'This garment was cancelled. Restore it before moving it');
  }

  if (from === to) return refuse('same_stage', 'This garment is already at that stage');

  if (!isEnabled(to, enabledStages)) {
    return refuse('stage_disabled', 'That stage is not part of this shop’s workflow');
  }

  const forward = STAGE_ORDINAL[to] > STAGE_ORDINAL[from];

  if (forward) {
    if (!isProgressiveStage(from)) {
      return refuse('terminal_stage', 'Nothing follows this stage');
    }
    const next = nextStage(from, enabledStages);
    if (next === undefined) {
      return refuse('terminal_stage', 'This garment has reached the end of the workflow');
    }
    if (next !== to) {
      // The named skip, refused by name: a shop that really has finished the intervening work
      // records it, one stage at a time, and keeps its own timings honest.
      return refuse('skips_stages', `Move it to ${next} first — stages cannot be skipped`);
    }
    return { ok: true, kind: 'advance', from, to, requiresReason: false };
  }

  const reason = request.reason?.trim() ?? '';
  if (reason.length === 0) {
    return refuse('reason_required', 'Say why this garment is going back');
  }

  return {
    ok: true,
    kind: request.rework === true ? 'rework' : 'revert',
    from,
    to,
    requiresReason: true,
  };
}

export interface StageMoveOptions {
  /** The one stage a forward move may go to, or `undefined` at the end of the line. */
  readonly advanceTo: WorkflowStage | undefined;
  /** Every earlier enabled stage, nearest first — the rework targets. */
  readonly revertTo: readonly WorkflowStage[];
}

/**
 * What the controls should offer for an item at `stage`. The mobile stage control renders
 * exactly this, so the board can never present a move the API would refuse.
 */
export function stageMoveOptions(
  stage: WorkflowStage,
  enabledStages: readonly WorkflowStage[] = STAGE_SEQUENCE,
): StageMoveOptions {
  if (stage === 'cancelled') return { advanceTo: undefined, revertTo: [] };

  return {
    advanceTo: nextStage(stage, enabledStages),
    revertTo: enabledStages
      .filter((candidate) => STAGE_ORDINAL[candidate] < STAGE_ORDINAL[stage])
      .sort((a, b) => STAGE_ORDINAL[b] - STAGE_ORDINAL[a]),
  };
}

/** True when a move from `from` to `to` needs a reason — i.e. it goes backwards. */
export function stageMoveNeedsReason(from: WorkflowStage, to: WorkflowStage): boolean {
  return STAGE_ORDINAL[to] < STAGE_ORDINAL[from] && to !== 'cancelled' && from !== 'cancelled';
}

/**
 * Which stages ONE garment type walks.
 *
 * The browser's copy of the rule the API applies in `StageConfiguration.stagesFor`, and it has
 * to be the same rule: the board decides from this which control to offer, and an answer that
 * disagreed with the server would put a button on a card that the API then refuses.
 *
 * A garment type with no flow of its own walks the shop's whole workflow — which is every
 * garment type in every shop that has not customised one. A flow is always intersected with
 * what the shop currently has enabled, so a flow naming a stage since switched off cannot
 * resurrect it, and the stages every garment must walk are added back whatever the flow says.
 *
 * The parameter is structural rather than the `StageConfigurationView` type so that this file
 * stays free of the schema module, which imports from here.
 */
export function stagesForGarment(
  configuration:
    | {
        readonly enabledStages: readonly WorkflowStage[];
        readonly flows?: readonly { readonly garmentTypeKey: string; readonly stages: readonly WorkflowStage[] }[]; // prettier-ignore
      }
    | null
    | undefined,
  garmentTypeKey: string | null | undefined,
): readonly WorkflowStage[] {
  const enabled = configuration?.enabledStages ?? STAGE_SEQUENCE;
  if (!garmentTypeKey) return enabled;

  const flow = configuration?.flows?.find((one) => one.garmentTypeKey === garmentTypeKey);
  if (!flow) return enabled;

  const chosen = new Set(flow.stages);
  return enabled.filter((stage) => chosen.has(stage) || REQUIRED_STAGES.includes(stage));
}
